import { registerBlock } from "../registry";
import { makeId } from "../helpers";
import type { ContentBlock, ContentTag, ArticleBody } from "../types";

// ─── Helper ─────────────────────────────────────────────────────────

function moonArticle(
  slug: string,
  opts: {
    title: string;
    time: string;
    tag: string;
    teaser: string;
    body: ArticleBody;
    sectionTags: ContentTag[];
  },
): ContentBlock<"article"> {
  return {
    id: makeId(`moon-article-${slug}`),
    kind: "article",
    tags: ["moon", ...opts.sectionTags],
    title: opts.title,
    subtitle: opts.teaser,
    readTimeMinutes: parseInt(opts.time, 10) || undefined,
    body: opts.body,
  };
}

// ─── Articles ───────────────────────────────────────────────────────

const insuranceFertility = moonArticle("insurance-fertility", {
  title: "Insurance and fertility: what's actually covered",
  time: "8 min",
  tag: "Coverage",
  teaser: "State mandates, plan differences, and how to fight denials.",
  sectionTags: ["moon-costs"],
  body: {
    introduction: "Coverage",
    quickSummary: [
      {
        title: "21 states have mandates.",
        description:
          "21 states have some form of fertility insurance mandate — but coverage varies wildly.",
      },
      {
        title: "Coverage gaps are common.",
        description:
          'Even "fertility coverage" may exclude IVF, limit cycles, or require infertility diagnosis.',
      },
      {
        title: "Appeals work.",
        description:
          "You can appeal denials, and many patients win on appeal — persistence pays off.",
      },
    ],
    sections: [
      {
        title: "State mandates overview",
        content:
          'Not all mandates are created equal. Some states require insurers to cover fertility treatment, while others only require them to offer it as an optional rider. The difference matters enormously. "Mandate to cover" means your plan must include it; "mandate to offer" means your employer can choose whether to add it. States like Illinois, Massachusetts, and Connecticut have strong mandates that include IVF. Others mandate coverage for diagnostics only, or exclude IVF entirely.',
        bulletPoints: [
          "Mandate to cover: insurer must include fertility benefits (IL, MA, CT, NJ, MD, and others)",
          "Mandate to offer: employer can opt in or out (CA, TX, and others)",
          "Some states mandate coverage for fertility treatment but explicitly exclude IVF",
          "Self-funded employer plans (common at large companies) are exempt from state mandates under ERISA",
        ],
      },
      {
        title: "Understanding your specific plan",
        content:
          "Your Summary Plan Description (SPD) is the legal document that defines what your plan covers. Request it from HR or your insurance company and look for the fertility or infertility section specifically. Key terms to understand will determine how much you actually pay out of pocket.",
        bulletPoints: [
          "Lifetime maximum: the total dollar amount your plan will pay toward fertility treatment over your lifetime",
          "Cycle limits: some plans cap the number of IVF or egg freezing cycles covered",
          "Prior authorization: many plans require pre-approval before treatment begins",
          "Medical necessity: your doctor may need to document why treatment is medically necessary for coverage to kick in",
        ],
      },
      {
        title: "Common exclusions and surprises",
        content:
          "Even plans with fertility coverage often have surprising gaps. Many plans define infertility as failure to conceive after 12 months of unprotected intercourse, which can exclude same-sex couples and single parents by default. Some states have updated their definitions to be more inclusive, but many have not.",
        bulletPoints: [
          "Same-sex couples and single parents often excluded from traditional infertility definitions",
          "Medication may be covered under your pharmacy benefit separately from procedures",
          "PGT-A genetic testing is often not covered, even when IVF is",
          "Egg freezing for fertility preservation (without a diagnosis) is rarely covered by insurance",
        ],
      },
      {
        title: "How to appeal a denial",
        content:
          "If your claim is denied, you have the right to appeal — and many patients win on appeal. The process takes persistence, but the financial stakes make it worthwhile.",
        bulletPoints: [
          "Step 1: Get the denial in writing and understand the specific reason",
          "Step 2: Review your plan documents to confirm the denial is consistent with your coverage",
          "Step 3: Write a formal appeal with a medical necessity letter from your reproductive endocrinologist",
          "Step 4: If the internal appeal is denied, request an external review by an independent third party",
        ],
      },
      {
        title: "Working with your clinic's financial team",
        content:
          "Most fertility clinics have financial coordinators who deal with insurance companies daily. They can help you verify benefits, submit prior authorizations, and navigate appeals. Don't try to do this alone — lean on their expertise. Ask to speak with the financial team before your first cycle to understand exactly what will and won't be covered.",
      },
      {
        title: "Line-by-line cost breakdown (uncovered)",
        content:
          "If insurance doesn't apply, understanding each cost component helps you compare clinic quotes accurately and identify which items might still be partially covered by employer benefits or pharmacy savings programs.",
        bulletPoints: [
          "Monitoring (blood work + ultrasounds): $2,000–$4,000",
          "Retrieval procedure + anesthesia: $3,000–$7,000",
          "Medications: $2,000–$8,000 (varies by protocol and dosage)",
          "Fertilization + embryo culture: $1,500–$3,000",
          "PGT-A testing (optional): $3,000–$6,000",
          "Embryo transfer: $2,000–$4,000",
          "Frozen embryo transfer (subsequent cycles): $3,000–$5,000",
          "Annual storage: $500–$1,000 per year",
        ],
      },
      {
        title: "Financial strategies when paying out of pocket",
        content:
          "Many clinics offer payment plans or work with fertility-specific lenders. Some offer multi-cycle discount packages. Pharmacy savings programs (like Compassionate Care) can cut medication costs significantly. Don't be afraid to ask your clinic about financial options — they deal with this every day.",
        bulletPoints: [
          "Payment plans through fertility-specific lenders",
          "Multi-cycle discount packages",
          "Pharmacy savings programs (e.g. Compassionate Care)",
          "HSA/FSA accounts can be used for fertility treatments",
        ],
      },
    ],
  },
});

const employerBenefits = moonArticle("employer-benefits", {
  title: "Employer fertility benefits explained",
  time: "6 min",
  tag: "Benefits",
  teaser:
    "Progyny, Carrot, Maven, WINFertility — what they cover and how they work.",
  sectionTags: ["moon-costs"],
  body: {
    introduction: "Benefits",
    quickSummary: [
      {
        title: "More employers than ever.",
        description:
          "More employers than ever offer fertility benefits through third-party platforms.",
      },
      {
        title: "$10K–$100K+ available.",
        description:
          "Benefits range from $10K–$100K+ depending on the employer and platform.",
      },
      {
        title: "Fills insurance gaps.",
        description:
          "These benefits often cover what insurance doesn't — medications, genetic testing, and egg freezing.",
      },
    ],
    sections: [
      {
        title: "The major platforms",
        content:
          "Most employer fertility benefits are administered through specialized third-party platforms. Each works differently, and understanding the model helps you maximize your benefit.",
        bulletPoints: [
          'Progyny: uses a "smart cycles" model that typically covers full IVF including medications and PGT-A genetic testing — considered the most comprehensive',
          "Carrot: provides a flexible fertility fund that can be used for IVF, egg freezing, adoption, surrogacy, and other family-building paths",
          "Maven: focuses on care navigation and clinical guidance with some coverage for treatment costs",
          "WINFertility: uses a prior authorization model where treatments must be pre-approved before coverage applies",
        ],
      },
      {
        title: "How to check your employer's offering",
        content:
          "Many employees don't realize they have fertility benefits until they need them. Check your HR portal, benefits enrollment documents, or ask your HR representative directly. Open enrollment is typically the only time you can add or change coverage, so review your options carefully each year.",
      },
      {
        title: "What's typically covered",
        content:
          "Employer fertility benefits are designed to fill gaps that traditional insurance leaves open. The specific coverage depends on your employer's plan, but most platforms cover the core treatment costs.",
        bulletPoints: [
          "Fertility medications (often the largest single expense)",
          "Monitoring appointments including blood work and ultrasounds",
          "Egg retrieval and IVF procedures",
          "Genetic testing including PGT-A",
          "Egg freezing and embryo freezing",
        ],
      },
      {
        title: "What's typically not covered",
        content:
          "Even generous employer benefits have limits. Understanding what falls outside your benefit helps you budget for the full cost of treatment.",
        bulletPoints: [
          "Surrogacy costs beyond the medical procedures (legal fees, carrier compensation)",
          "International treatment at clinics outside the platform's network",
          "Wellness supplements and non-prescription products",
          "Travel and lodging for treatment (though some platforms are adding this)",
        ],
      },
      {
        title: "Navigating the prior authorization process",
        content:
          "Most platforms require prior authorization before treatment begins. This means your doctor submits a treatment plan, and the platform reviews and approves it before you start. The process typically takes a few days to a week. Start early — don't wait until you're ready to begin your cycle to initiate authorization.",
      },
      {
        title: "Questions to ask before starting",
        content:
          "Before your first appointment, get clarity on the details that will affect your out-of-pocket costs and treatment options.",
        bulletPoints: [
          "What is your total lifetime benefit amount?",
          "Does the benefit cover medications, or only procedures?",
          "Are there network restrictions on which clinics you can use?",
          "Does unused benefit roll over year to year, or is it use-it-or-lose-it?",
          "Can your partner or spouse also access fertility benefits through their employer?",
        ],
      },
    ],
  },
});

const talkToHr = moonArticle("talk-to-hr-about-fertility-benefits", {
  title: "How to talk to HR about fertility benefits",
  time: "5 min",
  tag: "Practical",
  teaser:
    "A simple script and the questions that reveal what your plan really covers.",
  sectionTags: ["moon-costs"],
  body: {
    introduction: "Practical",
    quickSummary: [
      {
        title: "You can start simple.",
        description:
          "You do not need to share personal medical details to ask for benefits information.",
      },
      {
        title: "Ask for the plan documents.",
        description:
          "The Summary Plan Description and benefits administrator will answer more than a general HR overview.",
      },
      {
        title: "Leave with next steps.",
        description:
          "Ask who to contact, what needs prior authorization, and when the benefit year resets.",
      },
    ],
    sections: [
      {
        title: "A simple way to open the conversation",
        content:
          'Try: "I am reviewing my fertility and family-building benefits. Could you point me to the plan documents and the person who can explain how the benefit works?" You can keep the conversation focused on the plan rather than your diagnosis, treatment history, or family plans.',
      },
      {
        title: "Questions to ask HR",
        content:
          "HR may not know every clinical detail, but they can usually help you find the right documents and administrator.",
        bulletPoints: [
          "Is there a fertility or family-building benefit separate from my health insurance?",
          "Who administers it, and where can I find the Summary Plan Description or benefit guide?",
          "Does the benefit cover treatment, medications, testing, storage, adoption, or surrogacy?",
          "Are there clinic-network rules, eligibility requirements, or waiting periods?",
          "What needs prior authorization, and who should my clinic contact?",
          "Does the benefit reset each year, and what happens to unused funds or cycles?",
        ],
      },
      {
        title: "Questions for the benefits administrator",
        content:
          "Once you have the administrator's contact information, ask them to walk through your specific plan. Get answers in writing when possible.",
        bulletPoints: [
          "What is the lifetime or annual maximum, and is it measured in dollars, cycles, or another unit?",
          "Are medications covered through a separate pharmacy benefit?",
          "Which clinics and pharmacies are in network?",
          "What happens if a treatment is denied, delayed, or only partly covered?",
          "Can a partner's employer benefit coordinate with this plan?",
        ],
      },
      {
        title: "End with a paper trail",
        content:
          "Save the benefit guide, contact names, authorization instructions, and the date of each conversation. If someone gives you a verbal answer, send a short follow-up email summarizing what you understood and ask them to correct anything that is wrong.",
      },
    ],
  },
});

const financingIvf = moonArticle("financing-ivf", {
  title: "Financing IVF: loans, grants, and payment plans",
  time: "7 min",
  tag: "Money",
  teaser: "When insurance doesn't cover it — options for paying out of pocket.",
  sectionTags: ["moon-costs"],
  body: {
    introduction: "Money",
    quickSummary: [
      {
        title: "$15K–$30K per cycle.",
        description:
          "Average out-of-pocket IVF cost is $15,000–$30,000 per cycle including medications.",
      },
      {
        title: "Multi-cycle programs exist.",
        description:
          "Multi-cycle and shared risk programs can lower per-cycle costs and cap total spending.",
      },
      {
        title: "Multiple funding sources.",
        description:
          "Fertility-specific loans, grants, and HSA/FSA dollars can all help — but read the fine print.",
      },
    ],
    sections: [
      {
        title: "Understanding total costs",
        content:
          "IVF is not one bill — it's many separate charges that add up quickly. Understanding each component helps you compare clinic quotes and identify where you might save.",
        bulletPoints: [
          "Clinic fee (monitoring, retrieval, transfer): $8,000–$15,000",
          "Anesthesia for retrieval: $500–$1,500",
          "Embryology lab (fertilization, culture, grading): $2,000–$4,000",
          "Medications (stimulation, trigger, support): $3,000–$8,000",
          "Monitoring (blood work and ultrasounds): $1,500–$3,000",
          "PGT-A genetic testing (optional): $3,000–$6,000",
          "Frozen embryo transfer (FET): $3,000–$5,000",
          "Annual embryo storage: $500–$1,200/year",
        ],
      },
      {
        title: "Clinic payment plans and multi-cycle packages",
        content:
          'Many clinics offer bundled pricing for multiple cycles, which lowers the per-cycle cost. Shared risk (or refund) programs charge a higher upfront fee but refund a portion if treatment is unsuccessful. These can be a good deal if you anticipate needing multiple cycles, but understand what "refund" really means — it typically excludes medications and some fees, and you must meet eligibility criteria (usually age and AMH-based).',
        bulletPoints: [
          "Bundled pricing: pay upfront for 2–3 cycles at a discounted rate",
          "Shared risk programs: higher upfront cost with partial refund if no live birth",
          'Read the fine print — "refund" rarely means 100% of what you paid',
          "Eligibility often restricted by age (typically under 38–40) and ovarian reserve",
        ],
      },
      {
        title: "Fertility loans and financing",
        content:
          "Several lenders specialize in fertility treatment financing. Interest rates and terms vary, so compare carefully before committing.",
        bulletPoints: [
          "Prosper Healthcare Lending: fixed rates, terms up to 84 months, no prepayment penalty",
          "CapexMD: fertility-specific lender with flexible payment plans",
          "Future Family: combines financing with a fertility coaching program",
          "Compare APRs carefully — rates range from 5% to 25%+ depending on credit",
          "Some clinics partner with specific lenders and may offer promotional rates",
        ],
      },
      {
        title: "Grants and nonprofit assistance",
        content:
          "Several organizations offer grants to help cover fertility treatment costs. Most are competitive and require an application process, but they can provide significant financial relief.",
        bulletPoints: [
          "Baby Quest Foundation: grants up to $16,000 for IVF, egg freezing, and other treatments",
          "The Cade Foundation: family-building grants for those who cannot afford treatment",
          "AGC Scholarships: financial assistance for fertility treatment and adoption",
          "Many grants have specific eligibility criteria — apply to multiple programs to improve your chances",
        ],
      },
      {
        title: "Tax strategies",
        content:
          "Fertility treatment qualifies for several tax advantages that can meaningfully reduce your effective cost.",
        bulletPoints: [
          "HSA and FSA accounts can be used for fertility treatment, medications, and related expenses",
          "Medical expense deduction: if total medical expenses exceed 7.5% of your AGI, the excess is deductible",
          "Dependent care FSA does not apply to fertility treatment — only post-birth childcare",
          "Keep every receipt and EOB — you'll need documentation for tax purposes",
        ],
      },
      {
        title: "International treatment as cost-saving",
        content:
          "Some patients travel abroad for IVF at significantly lower costs. Clinics in Mexico, Czech Republic, and Spain offer IVF cycles at 40–60% of US prices with comparable success rates at top-tier facilities. However, this approach requires careful consideration of logistics (travel during monitoring, time off work), legal implications (especially for genetic testing and surrogacy), and quality verification (look for clinics with international accreditation and published success rates).",
      },
    ],
  },
});

const hiddenCosts = moonArticle("hidden-costs", {
  title: "The hidden costs nobody mentions",
  time: "5 min",
  tag: "Real talk",
  teaser:
    "Storage fees, add-ons, and the expenses that catch people off guard.",
  sectionTags: ["moon-costs"],
  body: {
    introduction: "Real talk",
    quickSummary: [
      {
        title: "Storage adds up.",
        description:
          "Annual embryo/egg storage fees ($500–$1,200/year) add up over time.",
      },
      {
        title: "Add-ons inflate costs.",
        description:
          '"Add-ons" like PGT-A, assisted hatching, endometrial scratch, and ICSI can add $3,000–$10,000 per cycle.',
      },
      {
        title: "Beyond the clinic bill.",
        description:
          "Lost income, travel, and emotional support costs are real but rarely discussed.",
      },
    ],
    sections: [
      {
        title: "Ongoing storage fees",
        content:
          "Once you freeze eggs or embryos, annual storage fees begin — and they don't stop until you use, donate, or discard them. Most patients don't think about this as a long-term cost, but over 5–10 years it adds up significantly.",
        bulletPoints: [
          "Embryo storage: $500–$1,200 per year depending on clinic and location",
          "Egg storage: similar range, sometimes slightly lower",
          "Sperm storage: $300–$800 per year",
          "If you stop paying, clinics will attempt to contact you — but policies on abandoned specimens vary",
          "Disposition decisions (what to do with unused embryos) are emotionally and financially complex",
        ],
      },
      {
        title: "Medication cost surprises",
        content:
          "Medications are often the most variable cost in a cycle, and small changes in protocol can mean thousands of dollars difference. Shopping around for your fertility medications can save significant money.",
        bulletPoints: [
          "Brand vs. generic: generics are available for some medications and can save 30–50%",
          "Pharmacy shopping: specialty pharmacies often beat your clinic's pharmacy by $1,000+",
          "International pharmacies: some patients order from verified international pharmacies at significant savings",
          "Manufacturer compassionate use programs: drug companies sometimes offer discounts or free medication for qualifying patients",
        ],
      },
      {
        title: "The add-on costs",
        content:
          "Clinics may recommend additional procedures and tests beyond the base IVF cycle. Some are well-supported by evidence; others are more controversial. Understanding which is which helps you make informed decisions about where to spend.",
        bulletPoints: [
          "PGT-A (genetic testing): $3,000–$6,000 — strong evidence for patients over 35 or with recurrent loss",
          "ICSI (intracytoplasmic sperm injection): $1,500–$3,000 — necessary for male factor, debated for routine use",
          "Assisted hatching: $500–$1,500 — limited evidence for most patients",
          "Endometrial scratch: $150–$500 — mixed evidence, falling out of favor",
          "ERA (endometrial receptivity analysis): $800–$1,200 — may help after repeated implantation failure",
        ],
      },
      {
        title: "The time cost",
        content:
          "Fertility treatment demands a significant time investment that's rarely factored into the overall cost. The schedule is unpredictable, and most appointments happen during business hours.",
        bulletPoints: [
          "Monitoring appointments every 2–3 days during stimulation, typically early morning",
          "Travel to and from the clinic adds up, especially if your clinic isn't nearby",
          "Recovery days after retrieval (most patients take 1–2 days off)",
          "Emotional bandwidth — the mental load of treatment affects productivity and wellbeing",
        ],
      },
      {
        title: "Lost income and career impact",
        content:
          "This is the cost almost nobody talks about openly. Taking time off for appointments, dealing with side effects, and managing the emotional weight of treatment all affect your work. Some patients reduce their hours, turn down projects, or delay career moves during treatment. There's no easy way to quantify this, but it's real — and it's worth factoring into your planning.",
      },
      {
        title: "The cost of multiple cycles",
        content:
          "Most patients need 2–3 IVF cycles to achieve a live birth. Planning ahead financially — rather than treating each cycle as a one-time expense — can reduce stress and help you make better decisions about multi-cycle packages, financing, and when to continue or pause treatment.",
        bulletPoints: [
          "Average number of cycles to live birth: 2.5 for patients under 35, higher for older patients",
          "Multi-cycle packages can save 20–30% compared to paying per cycle",
          "Factor in FET costs if you bank embryos from multiple retrievals",
          "Build a financial cushion for the unexpected — canceled cycles, additional testing, and protocol changes",
        ],
      },
    ],
  },
});

// ─── Register all Costs articles ────────────────────────────────────

const allNewCosts = [
  insuranceFertility,
  employerBenefits,
  talkToHr,
  financingIvf,
  hiddenCosts,
];
for (const article of allNewCosts) {
  registerBlock(article);
}
